import moment from 'moment'

import { store } from 'config/redux'
import { strings, formatString } from 'context/LanguageContext/strings'
import { util } from 'utils'
import { alcoholUtility } from 'utils/alcoholutils'
import { getCurrentCountry, isDeliveryMode } from 'utils/appUtils'
import { payment } from 'utils/paymentUtils'

export const parseBasketResponse = response => {
    if (!response) {
        return {
            canCheckout: false,
            isETAPresent: false,
            ETA: '',
            basketSummary: [],
            items: [],
            promoCodeResponse: null,
            referralCodeResponse: null,
        }
    }

    const basketResponse = {}

    const canCheckout = response.status && response.status.toUpperCase() === ('READY' || 'CHECKOUT')
    basketResponse.canCheckout = canCheckout

    const { isETAPresent, ETA } = getETADetails(response)
    basketResponse.isETAPresent = isETAPresent
    basketResponse.ETA = ETA

    basketResponse.basketSummary = parseBasketSummary(response.basket_summary)
    basketResponse.totalBeforeTax = getTotalBeforeTax(response.basket_summary)
    basketResponse.subtotal = response.subtotal ? response.subtotal : null
    //TO DO: Parsing logic to have Promotion? Should it be kept separate?
    basketResponse.promoCodeResponse =
        (response.coupon_code_response && response.coupon_code_response[0]) || null
    basketResponse.items = response.items || []
    basketResponse.convenience_fee = response.convenience_fee || 0
    basketResponse.referralCodeResponse =
        response && response.referral && parseReferalResponse(response)
    basketResponse.subscription = {
        is_user_subscribed: response.is_user_subscribed,
        subscriptions_promotions: response.subscriptions_promotions,
        subscription_id: response.subscription_id,
        subscription_discount_amount: response.subscription_discount_amount,
        subscription_discount_banner: response.subscription_discount_banner,
        subscription_promo_item_banner: response.subscription_promo_item_banner,
    }
    basketResponse.smallBasketFeeDetails = {
        avoidSmallBasketFeeByAdding: response.avoid_basket_fee_difference,
        smallBasketFeeWarning: response.basket_fee_banner,
    }
    const basketUpdates = checkInvalidItems(response)

    return { basketResponse, basketUpdates }
}

export const parseCheckoutResponse = response => {
    if (!response) {
        return {
            cart_hash: null,
        }
    }

    const checkoutResponse = {
        ...response,
        summary: parseBasketSummary(response.summary),
    }

    return checkoutResponse
}

const parseBasketSummary = basket_summary => {
    const basketSummary = []

    if (basket_summary && basket_summary.length > 0) {
        for (let loop = 0; loop < basket_summary.length; loop++) {
            const summaryItem = basket_summary[loop]
            const parsedSummaryItem = getParsedSummaryItem(summaryItem)
            if (!parsedSummaryItem) continue

            const newSummaryItem = {
                ...parsedSummaryItem,
            }

            if (summaryItem.roll_up_flag && summaryItem.details) {
                //Check for tootip
                let toolTipData = []
                for (let innerLoop = 0; innerLoop < summaryItem.details.length; innerLoop++) {
                    let toolTipItem = summaryItem.details[innerLoop]
                    let parsedToolTipItem = getParsedSummaryItem(toolTipItem)
                    if (parsedToolTipItem) toolTipData.push(parsedToolTipItem)
                }

                toolTipData.push(newSummaryItem) //To show the final line in the tool tip as well.
                parsedSummaryItem.toolTipData = toolTipData
            }
            basketSummary.push(parsedSummaryItem)
        }
    }
    return basketSummary
}

const getTotalBeforeTax = basket_summary => {
    if (basket_summary && basket_summary.length > 0) {
        let basketSummaryItem = basket_summary.find(item => item.key === 'total_before_tax')
        return {
            name: basketSummaryItem.display_name,
            value: basketSummaryItem.display_value,
            isBold: basketSummaryItem.font_weight_bold === true ? true : false,
        }
    }
    return null
}

const parseReferalResponse = payload => {
    const getReferralStatus = redemption_response => {
        if (redemption_response?.redeemed) {
            return 'Referral Code successfully applied'
        }
        return redemption_response?.error_message || 'Referral code is invalid'
    }

    const referralResponse = {
        referral_code: payload?.referral?.referral_code,
        source: payload?.referral?.source,
        redemption_response: {
            status:
                payload?.referral?.redemption_response?.status ||
                getReferralStatus(payload?.referral?.redemption_response),
            redeemed: payload?.referral?.redemption_response?.redeemed,
        },
    }

    return referralResponse
}

const getParsedSummaryItem = item => {
    //Do not include the total before tax as a line item in the basket summary
    if (
        !item ||
        !item.key ||
        !item.display_name ||
        !item.display_value ||
        item.key === 'total_before_tax'
    )
        return null
    return {
        name: item.display_name,
        value: item.display_value,
        message: item.display_message || '',
        isBold: item.font_weight_bold === true ? true : false,
        isStrikeThrough: item.strike_through === true ? true : false,
    }
}

const getETADetails = cartResponse => {
    if (isDeliveryMode()) {
        if (cartResponse.shipping && cartResponse.shipping.quote && cartResponse.eta_range) {
            return {
                isETAPresent: true,
                ETA: cartResponse.eta_range,
            }
        }
    } else {
        if (cartResponse && cartResponse.pickup_eta) {
            const TimeFormatTemplate = 'h:mm A'
            const eta = moment(cartResponse.pickup_eta).format(TimeFormatTemplate)

            return {
                isETAPresent: true,
                ETA: eta,
            }
        }
    }

    return {
        isETAPresent: false,
        ETA: '',
    }
}

const checkInvalidItems = response => {
    if (!response.items) return []
    const { basket } = store.getState()
    const localItems = basket.toJS().basketItems

    let invalids = {}
    const quantityUpdated = response.items
        .filter(item => item.available && item.quantity > item.availableQuantity)
        .map(item => {
            const message = formatString('basketUpdateQuantityItem', {
                0: item.quantity,
                1: item.availableQuantity,
            })
            return {
                product: { ...item, updatedQuantity: item.availableQuantity },
                displayMessage: message,
            }
        })

    if (quantityUpdated.length > 0) {
        invalids.quantityUpdated = quantityUpdated
    }

    let itemsUnavailable = response.items
        .filter(item => !item.available || alcoholUtility.isAlcoholRestricted(item))
        .map(item => {
            return {
                product: { ...item, updatedQuantity: item.quantity },
                displayMessage: strings.basketUpdateUnavailable,
            }
        })

    if (itemsUnavailable.length > 0) {
        invalids.itemsUnavailable = itemsUnavailable
    }

    let priceMismatches = getPriceMismatches(localItems, response.items)
    if (priceMismatches.length > 0) {
        invalids.priceMismatches = priceMismatches
    }
    return invalids
}

const getPriceMismatches = (localItems, remoteItems) => {
    let priceMismatches = []
    for (let loop = 0; loop < remoteItems.length; loop++) {
        const remoteItem = remoteItems[loop]
        //If item is not available, it should be a part of the Unavailable
        if (!remoteItem.available || (remoteItem.item_discount && remoteItem.item_discount > 0))
            continue

        const localItem = localItems.find(
            item => item.product_id === remoteItem.product_id && remoteItem.available
        )

        if (!localItem) continue
        const remoteItemPrice = util.getPrice(remoteItem)
        const localItemPrice = util.getPrice(localItem)

        if (remoteItemPrice !== localItemPrice) {
            const priceDiff = Math.abs(remoteItemPrice - localItemPrice)
            const priceIncreased = Math.abs(remoteItemPrice > localItemPrice)
            let message
            message = strings.basketUpdatePrices

            const priceMismatch = {
                product: localItem,
                displayMessage: message,
                priceDiff,
                priceIncreased,
            }
            priceMismatches.push(priceMismatch)
        }
    }
    return priceMismatches
}

export const parseBasketItems = (remoteItems, basketUpdates) => {
    //Overwrite Availablee Remote items here, and any invalids are shown in basket updates
    //Remove any unavailable for Alcohol
    const itemsUnavailable = basketUpdates && basketUpdates.itemsUnavailable
    const basketItems = remoteItems
        // .filter(remote => {
        //     const itemNotPresent =
        //         itemsUnavailable &&
        //         itemsUnavailable.find(item => item.product.product_id === remote.product_id)
        //     return remote.available && !itemNotPresent ? true : false
        // })
        .map(item => ({
            count: item.quantity,
            ...item,
            discountedPrice: getDiscountedPrice(item),
        }))
    return basketItems
}

const getDiscountedPrice = item => {
    if (item && item.quantity && item.item_discount) {
        return item.quantity * util.getPrice(item) - item.item_discount
    }
    return null
}

export const suggestiveSellingRequest = (store_id, basketItems) => {
    const productIds = basketItems.map(item => {
        return item.product_id
    })

    return {
        store_id,
        product_id: productIds,
        version: 1,
        time_of_day: new Date().getHours(),
        day_of_week: new Date().getDay(),
    }
}

export const parseSuggestiveSellingResponse = response => {
    let items = []
    if (response && response.items && response.items.length > 0) {
        items = response.items.map(item => {
            const parsedItem = util.parseProduct(item)
            if (parsedItem) return parsedItem
            return
        })
    }

    return items
}

export const getPaymentDetails = (card, store_id, customer_id, amount) => {
    const { configuration, location, user } = store.getState()
    let companyId = configuration.toJS().company_id
    let currency = configuration.toJS().currency
    const payment_mode = payment.getPaymentMode(card?.id || card?.brand)

    if (!currency) {
        const countryCode = getCurrentCountry(location, user)
        if (countryCode === countryCode.CA) {
            currency = 'cad'
        } else {
            currency = 'usd'
        }
    }

    if (!companyId) {
        const countryCode = getCurrentCountry(location, user)
        if (countryCode === countryCode.CA) {
            companyId = '124'
        } else {
            companyId = '125'
        }
    }

    let details = {
        company_id: companyId,
        currency,
        payment_mode: payment_mode,
        gateway: card.gateway,
        amount,
        store_id,
        customer_id,
    }

    if (payment_mode === 'paypal' || payment_mode === 'venmo') {
        details = {
            ...details,
            payment_customer_id: card.customerId,
            payment_method_id: card.token,
            payment_mode: card.brand,
            device_data: 'N/A',
        }
    } else {
        details = {
            ...details,
            brand: card.brand,
            card: card.last4,
            card_id: card.id,
            exp_year: card.exp_year,
            exp_month: card.exp_month,
            stripe_cust_id: card.customer,
        }
    }

    return details
}
